// src/pages/auth/LoginPage.jsx
// ============================================================
// FIX SUMMARY:
//   1. useEffect is declared UNCONDITIONALLY at the top — not
//      after any early return or inside a condition block.
//      This directly fixes "React Hook called conditionally".
//   2. The early-return redirect check is now in the useEffect,
//      not as an if-block before hooks.
//   3. Loading overlay covers the form while auth state resolves,
//      preventing flicker.
//   4. setIsLoading(false) always runs (finally block) — no
//      hanging spinner on error.
// ============================================================

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useOrgSettings } from "../../hooks/useFirestore";
import { getDashboardPath } from "../../components/common/ProtectedRoute";

export default function LoginPage() {
  const { login, resetPassword, currentUser, userProfile, loading, deactivatedError, setDeactivatedError } = useAuth();
  const { data: orgSettings } = useOrgSettings();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [resetMsg, setResetMsg] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  // ── FIX: useEffect is ALWAYS called here, never conditionally ──
  // This redirects an already-logged-in user to their dashboard.
  // Putting this in useEffect (rather than as an early return above
  // the hooks) ensures hooks are never skipped.
  useEffect(() => {
    if (!loading && currentUser && userProfile) {
      // Never redirect a deactivated account
      if (
        deactivatedError ||
        userProfile.isActive === false ||
        userProfile.status === "Inactive" ||
        userProfile.status === "Deactivated" ||
        userProfile.status === "Suspended"
      ) {
        return;
      }
      navigate(getDashboardPath(userProfile.role), { replace: true });
    }
  }, [loading, currentUser, userProfile, navigate, deactivatedError]);

  // ── Handle login form submit ─────────────────────────────
  async function handleLogin(e) {
    e.preventDefault();
    setFormError("");
    setFieldErrors({});
    setDeactivatedError("");

    const errors = {};
    if (!email.trim()) errors.email = "Email is required.";
    if (!password) errors.password = "Password is required.";
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsSubmitting(true);
    if (!email.trim() || !password) {
      setFormError("Both email and password are required.");
      setIsSubmitting(false);
      return;
    }
    try {
      await login(email.trim(), password);
      // onAuthStateChanged in AuthContext will fire, update userProfile,
      // and the useEffect above will trigger the redirect automatically.
    } catch (err) {
      if (err.code === "auth/user-deactivated") {
        // deactivatedError is already set in AuthContext; nothing extra needed
      } else if (err.code === "auth/user-not-found" || err.code === "auth/invalid-email") {
        setFieldErrors({ email: "Invalid email address." });
      } else if (err.code === "auth/wrong-password") {
        setFieldErrors({ password: "Invalid password. Please try again." });
      } else if (err.code === "auth/invalid-credential") {
        setFieldErrors({ password: "Invalid email or password." });
      } else {
        setFormError(friendlyError(err.code));
      }
    } finally {
      // Always release the button — even on success (component may
      // unmount from redirect before this runs, which is fine)
      setIsSubmitting(false);
    }
  }

  // ── Handle forgot-password form submit ───────────────────
  async function handleReset(e) {
    e.preventDefault();
    setFormError("");
    setResetMsg("");
    setFieldErrors({});

    if (!email.trim()) {
      setFieldErrors({ email: "Email is required." });
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPassword(email.trim());
      setResetMsg("Reset email sent! Check your inbox (and spam folder).");
    } catch (err) {
      if (err.code === "auth/user-not-found" || err.code === "auth/invalid-email") {
        setFieldErrors({ email: "Invalid email address." });
      } else {
        setFormError(friendlyError(err.code));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  // ── While auth is still initialising, show full-screen loader ──
  // This prevents the login form from flashing before we know
  // the user is already logged in.
  if (loading) {
    return (
      <div className="hrms-loading-screen">
        <div className="hrms-spinner" />
        <p className="hrms-loading-msg">Loading…</p>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="login-page">
      <div className="login-container">
        {/* Brand Header matching Image 1 */}
        <div className="login-header">
          <div className="login-logo-badge">
            <svg viewBox="0 0 120 120" fill="none" className="login-logo-svg" aria-label="Trendlance Innovations Logo">
              <defs>
                <linearGradient id="tl-login-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#8B5CF6" />
                  <stop offset="50%" stopColor="#A855F7" />
                  <stop offset="100%" stopColor="#EC4899" />
                </linearGradient>
              </defs>
              <rect x="8" y="8" width="104" height="104" rx="28" fill="url(#tl-login-grad)" />
              <rect x="34" y="36" width="52" height="7.5" rx="3.75" fill="#FFFFFF" />
              <rect x="42" y="52" width="36" height="6.5" rx="3.25" fill="#FFFFFF" opacity="0.95" />
              <rect x="56.25" y="36" width="7.5" height="48" rx="3.75" fill="#FFFFFF" />
            </svg>
          </div>
          <h1 className="login-brand-title">{orgSettings?.orgName?.toUpperCase() || "TRENDLANCE"}</h1>
          <span className="login-brand-subtitle">HRMS PORTAL</span>
        </div>

        {/* Login Card matching Image 1 */}
        <div className="login-card">
          <div className="login-card-header">
            <h2 className="login-card-title">
              {showReset ? "Reset Password" : "Welcome back"}
            </h2>
            <p className="login-card-subtitle">
              {showReset
                ? "Enter your email and we'll send you a reset link."
                : "Sign in to continue to your HRMS workspace"}
            </p>
          </div>

          {/* Feedback banners */}
          {deactivatedError ? (
            <div className="login-deactivated-banner" role="alert">
              <span className="login-deactivated-icon">🚫</span>
              <div>
                <strong>Account Deactivated</strong>
                <p>Your account has been deactivated. Please contact the administrator.</p>
              </div>
            </div>
          ) : formError ? (
            <div className="login-error" role="alert">{formError}</div>
          ) : null}
          {resetMsg && (
            <div className="login-success" role="status">{resetMsg}</div>
          )}

          {/* Form */}
          <form
            onSubmit={showReset ? handleReset : handleLogin}
            className="login-form"
            noValidate
          >
            {/* Work Email field */}
            <div className="login-form-group">
              <label htmlFor="lp-email">Work Email</label>
              <input
                id="lp-email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setFieldErrors(prev => ({ ...prev, email: "" })); }}
                placeholder="Enter your work email"
                autoComplete="email"
                disabled={isSubmitting}
                className={fieldErrors.email ? "input-error" : ""}
              />
              {fieldErrors.email && <div className="form-error">{fieldErrors.email}</div>}
            </div>

            {/* Password field */}
            {!showReset && (
              <div className="login-form-group">
                <label htmlFor="lp-password">Password</label>
                <div className="input-with-icon">
                  <input
                    id="lp-password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setFieldErrors(prev => ({ ...prev, password: "" })); }}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    disabled={isSubmitting}
                    className={fieldErrors.password ? "input-error" : ""}
                  />
                  <button
                    type="button"
                    className="input-icon-btn"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z" />
                        <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
                        <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 576 512">
                        <path d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z" />
                      </svg>
                    )}
                  </button>
                </div>
                {fieldErrors.password && <div className="form-error">{fieldErrors.password}</div>}
              </div>
            )}

            {/* Remember me & Forgot Password row */}
            {!showReset && (
              <div className="login-options-row">
                <label className="login-remember-me">
                  <input type="checkbox" defaultChecked />
                  <span>Remember me</span>
                </label>
                <button
                  type="button"
                  className="login-forgot-btn"
                  onClick={() => { setShowReset(true); setFormError(""); setFieldErrors({}); }}
                >
                  Forgot password?
                </button>
              </div>
            )}

            {/* Primary Action Button */}
            <button
              type="submit"
              className="login-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Please wait…"
                : showReset
                  ? "Send Reset Email"
                  : "Sign In"}
            </button>
          </form>

          {/* Reset password back button */}
          {showReset && (
            <div className="login-card-footer">
              <button
                type="button"
                className="login-forgot-btn"
                onClick={() => { setShowReset(false); setFormError(""); setResetMsg(""); setFieldErrors({}); }}
              >
                ← Back to sign in
              </button>
            </div>
          )}

          {/* Secure workplace badge matching Image 1 */}
          <div className="login-secure-badge">
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
            </svg>
            <span>Secure access to your workplace</span>
          </div>
        </div>

        {/* Footer copyright matching Image 1 */}
        <div className="login-footer-copyright">
          © {new Date().getFullYear()} {orgSettings?.orgName || "Trendlance Innovations"}. All rights reserved.
        </div>
      </div>
    </div>
  );
}

// ── Firebase error code → human-readable message ─────────
function friendlyError(code) {
  const map = {
    "auth/user-not-found": "Invalid email address.",
    "auth/wrong-password": "Invalid password. Please try again.",
    "auth/invalid-credential": "Invalid email or password.",
    "auth/invalid-email": "Invalid email address.",
    "auth/too-many-requests": "Too many failed attempts. Reset your password or try later.",
    "auth/user-disabled": "Your account has been deactivated. Please contact the administrator.",
    "auth/user-deactivated": "Your account has been deactivated. Please contact the administrator.",
    "auth/network-request-failed": "Network error. Check your connection and try again.",
  };
  return map[code] ?? "Something went wrong. Please try again.";
}
